// Loading skeleton for the Order and Event History pages. Keep in sync with
// Components::Skeletons::History, which server-renders this same markup for
// React to hydrate — a divergence swaps the DOM out from under the shimmer.
// Bar sizes are measured off the live page, so the skeleton settles into the
// real layout instead of jumping when the logs land.

import { MOBILE_CONTENT_PADDING, Page } from '../layouts/Page';
import { Bar, DEFAULT_LABEL } from './primitives';
import { cn } from '@/lib/utils';

const GROUP_COUNT = 2;
const CARDS_PER_GROUP = 2;

export function HistoryEntryCardSkeleton({ label }: { label?: string }) {
  // Only the labelled instance announces itself. The page skeleton passes no
  // label and announces once from its own wrapper.
  const announced = label ? { role: 'status', 'aria-busy': true, 'aria-label': label } : undefined;

  return (
    <div
      {...announced}
      className="rounded-input border border-dinnerplate-white bg-off-white p-6 shadow-soft"
    >
      <div className="flex w-full min-w-0 items-center gap-3 md:gap-6">
        <Bar className="h-[21px] w-[58px] shrink-0 md:w-23" />
        <div className="flex min-w-0 items-center gap-4">
          <Bar className="size-12 shrink-0 rounded-full" />
          <Bar className="h-[21px] w-[107px]" />
          <Bar className="h-[23px] w-[58px] rounded-xs" />
        </div>
      </div>
    </div>
  );
}

// The tail of the last group fades out, so the skeleton reads as a list that
// continues past the fold rather than one exactly two entries long. 50% lands
// on the middle of the second card: the column is 264px tall (12px padding +
// three 72px cards + two 12px gaps + 12px padding) and that midpoint is 132px.
// The mask covers the rail as well as the cards, so the rail does not stop flat.
const FADE =
  '[-webkit-mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)] ' +
  '[mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]';

function DayGroup({ fadeTail = false }: { fadeTail?: boolean }) {
  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center gap-4">
        <Bar className="size-12 shrink-0 rounded-full" />
        <Bar className="h-[22px] w-[144px]" />
      </div>
      <div className={cn('flex w-full gap-4', fadeTail && FADE)}>
        <div className="flex w-12 shrink-0 justify-center">
          <Bar className="w-[1.5px] rounded-full" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4 py-4">
          {Array.from({ length: CARDS_PER_GROUP }, (_, card) => (
            <HistoryEntryCardSkeleton key={card} />
          ))}
          {fadeTail ? <HistoryEntryCardSkeleton /> : null}
        </div>
      </div>
    </div>
  );
}

export function HistorySkeleton({ label = DEFAULT_LABEL }: { label?: string }) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      <Page>
        <Page.Header className="py-6 md:py-0">
          <div className={cn('flex w-full items-center', MOBILE_CONTENT_PADDING)}>
            <div className="flex flex-wrap items-center gap-4">
              <Bar className="h-[22px] w-[48px]" />
              <Bar className="h-[10px] w-[7px]" />
              <Bar className="h-[22px] w-[77px]" />
              <Bar className="h-[10px] w-[7px]" />
              <Bar className="h-[22px] w-[51px]" />
            </div>
          </div>
        </Page.Header>
        <Page.Body className={cn(MOBILE_CONTENT_PADDING, 'pt-0 md:pt-0')}>
          <div className="flex shrink-0 flex-wrap items-center gap-4 py-8">
            <Bar className="h-[45px] min-w-0 flex-1 rounded-input" />
            <Bar className="h-[47px] w-[137px] shrink-0 rounded-input" />
            <Bar className="h-[47px] w-[174px] shrink-0 rounded-input" />
          </div>
          <div className="flex w-full flex-col gap-4">
            {Array.from({ length: GROUP_COUNT }, (_, group) => (
              <DayGroup key={group} fadeTail={group === GROUP_COUNT - 1} />
            ))}
          </div>
        </Page.Body>
      </Page>
    </div>
  );
}
